import comecarIcon from '@cannect/assets/img/home/cta-icons/comecar.webp'
import cuidadoIcon from '@cannect/assets/img/home/cta-icons/cuidado.webp'
import medicoIcon from '@cannect/assets/img/home/cta-icons/medico.webp'
import produtosIcon from '@cannect/assets/img/home/cta-icons/produtos.webp'
import { Typography } from '@cannect/new-components/atoms'
import { RedirectUrls } from '@cannect/constants/texts'
import { CtaPill } from '@cannect/new-components/molecules'

type TStep = {
  title: string
  paragraphs: string[]
  /** `external` abre em nova aba — usado nos CTAs que levam ao WhatsApp. */
  cta: { title: string; path: string; external?: boolean; icon: string }
}

/**
 * As quatro etapas (Figma node 4333:585).
 *
 * Dois typos do arquivo foram corrigidos aqui: "que vcê precisa" na etapa 2 e "acompanhamento
 * continuo" na etapa 4 (sem acento). O resto da copy é literal.
 */
const STEPS: TStep[] = [
  {
    title: 'Entenda suas opções',
    paragraphs: [
      'Comece pela sua necessidade ou responda a algumas perguntas para descobrir o caminho mais adequado para uma vida melhor.',
      'Encontre aqui tudo o que precisa, seja importado ou da nossa farmácia.'
    ],
    cta: {
      title: 'Quero começar',
      path: RedirectUrls.WHATSAPP_TREATMENT_OPTIONS,
      external: true,
      icon: comecarIcon
    }
  },
  {
    title: 'Se precisar de uma consulta com um especialista, aqui é o lugar',
    paragraphs: [
      'Quando necessário, você irá encontrar profissionais preparados para a especialidade médica que você precisa, com agendas online ou consultas imediatas.'
    ],
    cta: { title: 'Preciso de um médico', path: '/agendamento', icon: medicoIcon }
  },
  {
    title: 'Já sabe o que precisa para seu tratamento?',
    paragraphs: [
      'Medicamentos importados e nacionais, terapias e produtos de saúde reunidos em uma experiência integrada.',
      'Através da nossa farmácia você encontra tudo o que precisa, incluindo produtos de higiene e beleza.'
    ],
    cta: { title: 'Já sei o que preciso', path: '/produtos', icon: produtosIcon }
  },
  {
    title: 'Continue sendo cuidado',
    paragraphs: [
      'Você sabia que mais de 60% dos pacientes crônicos no Mundo abandonam o tratamento?',
      'A chave do sucesso para uma saúde melhor sustentável é o acompanhamento contínuo.',
      'Nós estamos aqui por você!'
    ],
    cta: {
      title: 'Preciso de Cuidado',
      path: RedirectUrls.WHATSAPP_GUIDED_CARE,
      external: true,
      icon: cuidadoIcon
    }
  }
]

/**
 * Seção "Como funciona" da home (Figma node 4333:432).
 *
 * A seção está na segunda posição da página. As quatro etapas têm corpo de texto maior e um CTA
 * cada. Uma revisão anterior ocultou o CTA "Como começo?" do cabeçalho e o aviso azul do rodapé
 * ("Na nossa farmácia você encontra itens que não exigem consulta ou prescrição!"), o que levou a
 * seção de 806 para 702px.
 *
 * A revisão de 2026-07-31 centralizou o cabeçalho e trocou o H2 de "Cada etapa começa de onde a
 * anterior parou." para "Sua saúde é uma jornada contínua." (node 4333:2188).
 *
 * A régua numerada usa grid de quatro colunas igual à dos cards, em vez do `gap: 262px` fixo do
 * arquivo — assim cada número continua alinhado à borda do seu card em qualquer largura.
 *
 * A revisão mais recente inverteu o tema: a seção passou a ser `marketplace-1000` com os cards em
 * `marketplace-900/70`, título de card em `marketplace-500` e corpo em `neutral-50`. Os CTAs usam
 * a variante `outlineSoft`, criada para contorno discreto sobre o verde escuro.
 *
 * Contraste conferido contra o fundo do card: `marketplace-500` rende 5,43:1 e `neutral-50`
 * 10,44:1; sobre o fundo da seção, `neutral-50` rende 13,28:1. Todos passam WCAG AA.
 */
export const HowItWorksSection = () => (
  <section aria-labelledby="how-it-works-heading" className="w-full bg-marketplace-1000">
    <div className="mx-auto flex w-full max-w-[1230px] flex-col px-8 py-16">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center text-center">
          <p className="m-0 text-base leading-[1.6] text-neutral-50">Como funciona</p>

          <Typography.Heading
            id="how-it-works-heading"
            level={2}
            weight="regular"
            className="m-0 text-[28px] leading-[49.28px] tracking-[-0.448px] text-neutral-50">
            {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica.
                O ponto final fica em itálico branco, fora do `marketplace-500` — é assim no arquivo. */}
            Sua saúde é uma <span className="italic text-marketplace-500">jornada contínua</span>
            <span className="italic">.</span>
          </Typography.Heading>
        </div>

        {/* Altura fixa do bloco no design (474px): e ela que deixa os cards altos com o CTA
            ancorado no rodape. Sem isso os cards encolhem ao conteudo e a secao perde 143px. */}
        <div className="flex flex-col gap-4 lg:h-[474px]">
          {/* Régua: linha atrás dos números, alinhada ao centro dos círculos de 38px.
              `justify-items-center` porque no arquivo cada círculo fica no centro da coluna do seu
              card (centros em 137/433/729/1025 contra 138/434/730/1026 dos cards), não encostado à
              esquerda da célula — sem isso saem 119px fora de lugar. */}
          <div aria-hidden className="relative hidden lg:block">
            <span className="absolute left-0 top-[18px] h-px w-full bg-marketplace-800" />
            <ol className="relative m-0 grid list-none grid-cols-4 justify-items-center gap-5 p-0">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className="flex size-[38px] items-center justify-center rounded-full border border-solid border-marketplace-800 bg-marketplace-900 font-light italic leading-[28.16px] text-marketplace-500">
                  {index + 1}
                </li>
              ))}
            </ol>
          </div>

          <ol className="m-0 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 lg:min-h-0 lg:flex-1 lg:grid-cols-4">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className="flex flex-col justify-between gap-4 rounded-[16px] bg-marketplace-900/70 p-4">
                <div className="flex flex-col gap-2">
                  <h3 className="m-0 flex items-baseline gap-2 text-base font-medium leading-[28.16px] text-marketplace-500">
                    <span aria-hidden className="font-light italic text-marketplace-500 lg:hidden">
                      {index + 1}.
                    </span>
                    {step.title}
                  </h3>

                  {/* 20,8px entre parágrafos: no arquivo o corpo do card é um único bloco de texto
                      com uma linha em branco separando as partes (node 4333:600), então a distância
                      é uma linha inteira e não os 8px de um gap qualquer. */}
                  <div className="flex flex-col gap-[20.8px]">
                    {step.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="m-0 text-xs leading-[20.8px] text-neutral-50">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* `gap-3` (12px) entre rótulo e seta, contra os 32px que o CtaPill traz por padrão
                    — medida do CTA do Cannect Cuida, larga demais para estes cards.
                    O arquivo usa 16px aqui, mas nele a largura do CTA não inclui a borda de 1px:
                    com 16 sobram 138px para o rótulo e "Já sei o que preciso" precisa de 139, então
                    quebra em duas linhas onde o desenho mostra uma. Os 4px de diferença no gap
                    valem menos que a quebra de linha errada. */}
                <CtaPill
                  variant="outlineSoft"
                  full
                  title={step.cta.title}
                  path={step.cta.path}
                  external={step.cta.external}
                  icon={step.cta.icon}
                  className="gap-3"
                />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  </section>
)
